import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  BellOutlined,
  CheckCircleOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Avatar, Input, Modal, Select, Spin, Switch, Tag } from 'antd';
import rawCountries from '../../../../../assets/countries.json';
import { UtilService } from '../../../../../core/services/util.service';
import { User, UserId } from '../models/users.model';
import { UsersService } from '../services/users.service';
import { useProfile } from '../../profile/components/ProfileContext';
import { route } from '../../../../../constants/routes';
import './Company.css';
import './UserProfile.css';

interface CountryOption {
  value: string;
  name: string;
  flag: string;
  dialCode: string;
  label: JSX.Element;
}

const emptyUser = (): UserId => ({
  firstname: '',
  lastname: '',
  email: '',
  phoneNumber: '',
  enabled: true,
  admin: false,
  id: '',
  emailNotifiable: false,
  whatsappNotifiable: false,
  roles: [],
  deleted: false,
});

const displayName = (user: UserId) =>
  `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'New user';

const initials = (user: UserId) =>
  `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase() ||
  'US';

const roleName = (admin: boolean) =>
  admin ? 'Company administrator' : 'Recruiter';

const UserProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const { companyId, isAdmin, profile, setProfile } = useProfile();
  const isDeletedFromList = Boolean(location.state?.deleted);
  const isEditing = Boolean(userId);
  const isCurrentUser = Boolean(userId && profile?.id === userId);
  const [user, setUser] = useState<UserId>(emptyUser);
  const [initialFormState, setInitialFormState] = useState<UserId>(emptyUser);
  const [selectedCountryCode, setSelectedCountryCode] = useState('CR');
  const [profileImage, setProfileImage] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>(
    {}
  );
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const countryOptions = useMemo<CountryOption[]>(
    () =>
      rawCountries.map((country) => ({
        value: country.code,
        name: country.name,
        flag: country.flag,
        dialCode: country.dial_code,
        label: (
          <span className="user-profile__country-option">
            <img src={country.flag} alt="" />
            <span>
              {country.name} (+{country.dial_code})
            </span>
          </span>
        ),
      })),
    []
  );

  const selectedCountry =
    countryOptions.find((option) => option.value === selectedCountryCode) ||
    countryOptions.find((option) => option.value === 'CR') ||
    countryOptions[0];

  const readonly = isDeletedFromList || user.deleted;
  const canEditDetails = isAdmin && !readonly;
  const canEditOwnAccess = isAdmin && !readonly && !isCurrentUser;
  const hasChanges = JSON.stringify(user) !== JSON.stringify(initialFormState);

  const phoneLocalValue = useMemo(() => {
    if (!user.phoneNumber) return '';
    const normalized = user.phoneNumber.replace(/\s+/g, '');
    const prefix = `+${selectedCountry?.dialCode || ''}`;
    return normalized.startsWith(prefix)
      ? normalized.slice(prefix.length)
      : normalized.replace(/^\+/, '');
  }, [selectedCountry?.dialCode, user.phoneNumber]);

  useEffect(() => {
    if (!companyId || !userId) return;

    let active = true;
    const getUser = async () => {
      setLoading(true);
      try {
        const result = await UsersService.GetById(companyId, userId);
        if (!active || !result) return;
        setUser(result);
        setInitialFormState(result);

        const matchedCountry = countryOptions.find((option) =>
          result.phoneNumber?.startsWith(`+${option.dialCode}`)
        );
        if (matchedCountry) setSelectedCountryCode(matchedCountry.value);

        if (result.userFile?.file?.id) {
          try {
            const blob = await UsersService.GetFile(
              result.id,
              result.userFile.file.id
            );
            if (active) setProfileImage(URL.createObjectURL(blob));
          } catch (error: any) {
            if (error?.message !== 'NOT FOUND') {
              console.error('Failed to load user profile image:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        UtilService.Alert(
          'User unavailable',
          'error',
          'The user profile could not be loaded. Please try again.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void getUser();
    return () => {
      active = false;
    };
  }, [companyId, countryOptions, userId]);

  useEffect(
    () => () => {
      if (profileImage) URL.revokeObjectURL(profileImage);
    },
    [profileImage]
  );

  const validateUser = (candidate: UserId) => {
    const errors: string[] = [];
    const messages: Record<string, string> = {};

    if (!candidate.firstname?.trim()) {
      errors.push('firstname');
      messages.firstname = 'First name is required';
    }
    if (!candidate.lastname?.trim()) {
      errors.push('lastname');
      messages.lastname = 'Last name is required';
    }
    if (!candidate.email?.trim()) {
      errors.push('email');
      messages.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.email.trim())) {
      errors.push('email');
      messages.email = 'Enter a valid email address';
    }
    if (
      candidate.phoneNumber &&
      candidate.phoneNumber.replace(/\D/g, '').length < 5
    ) {
      errors.push('phoneNumber');
      messages.phoneNumber = 'Enter a valid phone number';
    }

    setValidationErrors(errors);
    setErrorMessages(messages);
    return errors.length === 0;
  };

  const updateField = (
    field: 'firstname' | 'lastname' | 'email',
    value: string
  ) => {
    setUser((current) => ({ ...current, [field]: value }));
    if (validationErrors.includes(field)) {
      setValidationErrors((errors) =>
        errors.filter((error) => error !== field)
      );
      setErrorMessages((messages) => {
        const next = { ...messages };
        delete next[field];
        return next;
      });
    }
  };

  const updateCountry = (countryCode: string) => {
    const nextCountry = countryOptions.find(
      (option) => option.value === countryCode
    );
    if (!nextCountry) return;
    setSelectedCountryCode(countryCode);
    const localNumber = phoneLocalValue.replace(/\D/g, '');
    setUser((current) => ({
      ...current,
      phoneNumber: localNumber ? `+${nextCountry.dialCode}${localNumber}` : '',
    }));
  };

  const updatePhone = (value: string) => {
    const localNumber = value.replace(/\D/g, '');
    setUser((current) => ({
      ...current,
      phoneNumber: localNumber
        ? `+${selectedCountry?.dialCode || '506'}${localNumber}`
        : '',
    }));
    if (validationErrors.includes('phoneNumber')) {
      setValidationErrors((errors) =>
        errors.filter((error) => error !== 'phoneNumber')
      );
      setErrorMessages((messages) => {
        const next = { ...messages };
        delete next.phoneNumber;
        return next;
      });
    }
  };

  const goBack = () => {
    const leave = () => navigate(route('/company/users'));
    if (!hasChanges) {
      leave();
      return;
    }
    Modal.confirm({
      title: 'Leave without saving?',
      content: 'Your changes to this user profile will be lost.',
      okText: 'Leave and discard',
      cancelText: 'Keep editing',
      okButtonProps: { danger: true },
      onOk: leave,
    });
  };

  const resetForm = () => {
    setUser(initialFormState);
    setValidationErrors([]);
    setErrorMessages({});
    const matchedCountry = countryOptions.find((option) =>
      initialFormState.phoneNumber?.startsWith(`+${option.dialCode}`)
    );
    if (matchedCountry) setSelectedCountryCode(matchedCountry.value);
  };

  const sendInvite = async () => {
    if (!validateUser(user) || !companyId) return;
    setSaving(true);
    try {
      await UsersService.Post(
        companyId,
        new User(
          user.firstname.trim(),
          user.lastname.trim(),
          user.email.trim(),
          user.admin
        )
      );
      UtilService.Alert(
        'Invitation sent',
        'success',
        `${displayName(user)} has been invited to the workspace.`
      );
      setUser(emptyUser());
      setInitialFormState(emptyUser());
      setValidationErrors([]);
      setErrorMessages({});
    } catch (error: any) {
      console.error('Error sending invite:', error);
      UtilService.Alert(
        'Invitation unavailable',
        'error',
        error?.message || 'The invitation could not be sent. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const updateUser = async () => {
    if (!validateUser(user) || !companyId || !userId) return;
    setSaving(true);
    const roles = user.admin ? ['COMPANY_ADMIN'] : ['COMPANY_RECRUITER'];
    const updatedUserState: UserId = {
      ...user,
      firstname: user.firstname.trim(),
      lastname: user.lastname.trim(),
      email: user.email.trim(),
      roles,
    };

    try {
      await UsersService.Put(companyId, userId, {
        firstname: updatedUserState.firstname,
        lastname: updatedUserState.lastname,
        email: updatedUserState.email,
        phoneNumber: updatedUserState.phoneNumber,
        active: updatedUserState.enabled,
        roles,
        whatsappNotifiable: updatedUserState.whatsappNotifiable,
        emailNotifiable: updatedUserState.emailNotifiable,
      });

      setUser(updatedUserState);
      setInitialFormState(updatedUserState);
      if (isCurrentUser && profile) {
        setProfile({
          ...profile,
          firstname: updatedUserState.firstname,
          lastname: updatedUserState.lastname,
          fullname: displayName(updatedUserState),
          email: updatedUserState.email,
          phoneNumber: updatedUserState.phoneNumber,
          emailNotifiable: updatedUserState.emailNotifiable,
          whatsappNotifiable: updatedUserState.whatsappNotifiable,
        });
      }
      UtilService.Alert(
        'User updated',
        'success',
        'The profile and access settings were saved.'
      );
    } catch (error: any) {
      console.error('Error updating user:', error);
      UtilService.Alert(
        'Update unavailable',
        'error',
        error?.message || 'The user could not be updated. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main
        className="user-profile-premium user-profile-premium--loading"
        aria-label="Loading user profile"
        aria-busy="true"
      >
        <Spin size="large" />
        <span>Loading user profile…</span>
      </main>
    );
  }

  return (
    <main className="user-profile-premium">
      <header className="user-profile__header">
        <button className="user-profile__back" type="button" onClick={goBack}>
          <ArrowLeftOutlined aria-hidden />
          Users
        </button>
        <div className="user-profile__heading">
          <div>
            <span>Team &amp; access</span>
            <h1>{isEditing ? 'Update user' : 'Invite user'}</h1>
            <p>
              {isEditing
                ? 'Keep identity, access, and communication preferences up to date.'
                : 'Invite a teammate and choose the right workspace access.'}
            </p>
          </div>
          <Tag
            className={`user-profile__status user-profile__status--${
              readonly ? 'archived' : user.enabled ? 'active' : 'inactive'
            }`}
          >
            {readonly ? 'Archived' : user.enabled ? 'Active' : 'Inactive'}
          </Tag>
        </div>
      </header>

      <div className="user-profile__layout">
        <div className="user-profile__content">
          <section className="user-profile__card">
            <div className="user-profile__card-header">
              <span className="user-profile__section-icon">
                <UserOutlined aria-hidden />
              </span>
              <div>
                <h2>Identity</h2>
                <p>The information used across the recruiting workspace.</p>
              </div>
            </div>

            <div className="user-profile__identity">
              <Avatar
                size={88}
                src={profileImage || undefined}
                icon={<UserOutlined />}
              >
                {!profileImage ? initials(user) : null}
              </Avatar>
              <div>
                <strong>{displayName(user)}</strong>
                <span>{roleName(user.admin)}</span>
                {isCurrentUser ? <small>This is your account</small> : null}
              </div>
            </div>

            <div className="user-profile__fields">
              <label className="user-profile__field">
                <span>First name</span>
                <Input
                  autoFocus={!isEditing}
                  placeholder="First name"
                  value={user.firstname}
                  status={validationErrors.includes('firstname') ? 'error' : ''}
                  disabled={isEditing && !canEditDetails}
                  onChange={(event) =>
                    updateField('firstname', event.target.value)
                  }
                />
                {validationErrors.includes('firstname') ? (
                  <small>{errorMessages.firstname}</small>
                ) : null}
              </label>
              <label className="user-profile__field">
                <span>Last name</span>
                <Input
                  placeholder="Last name"
                  value={user.lastname}
                  status={validationErrors.includes('lastname') ? 'error' : ''}
                  disabled={isEditing && !canEditDetails}
                  onChange={(event) =>
                    updateField('lastname', event.target.value)
                  }
                />
                {validationErrors.includes('lastname') ? (
                  <small>{errorMessages.lastname}</small>
                ) : null}
              </label>
            </div>
          </section>

          <section className="user-profile__card">
            <div className="user-profile__card-header">
              <span className="user-profile__section-icon">
                <IdcardOutlined aria-hidden />
              </span>
              <div>
                <h2>Contact information</h2>
                <p>Email and phone used for recruiting communication.</p>
              </div>
            </div>

            <div className="user-profile__fields">
              <label className="user-profile__field">
                <span>Email address</span>
                <div className="user-profile__field-action">
                  <Input
                    type="email"
                    placeholder="name@company.com"
                    prefix={<MailOutlined aria-hidden />}
                    value={user.email}
                    status={validationErrors.includes('email') ? 'error' : ''}
                    disabled={isEditing && !canEditDetails}
                    onChange={(event) =>
                      updateField('email', event.target.value)
                    }
                  />
                  {user.email ? (
                    <a href={`mailto:${user.email}`} aria-label="Send email">
                      <MailOutlined aria-hidden />
                    </a>
                  ) : null}
                </div>
                {validationErrors.includes('email') ? (
                  <small>{errorMessages.email}</small>
                ) : null}
              </label>

              {isEditing ? (
                <label className="user-profile__field">
                  <span>Phone number</span>
                  <div className="user-profile__phone">
                    <Select
                      aria-label="Country calling code"
                      options={countryOptions}
                      optionLabelProp="label"
                      value={selectedCountryCode}
                      disabled={!canEditDetails}
                      onChange={updateCountry}
                    />
                    <Input
                      type="tel"
                      placeholder="Phone number"
                      prefix={<PhoneOutlined aria-hidden />}
                      value={phoneLocalValue}
                      status={
                        validationErrors.includes('phoneNumber') ? 'error' : ''
                      }
                      disabled={!canEditDetails}
                      onChange={(event) => updatePhone(event.target.value)}
                    />
                    {user.phoneNumber ? (
                      <a
                        href={`https://wa.me/${user.phoneNumber.replace(
                          /\D/g,
                          ''
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open WhatsApp"
                      >
                        <WhatsAppOutlined aria-hidden />
                      </a>
                    ) : null}
                  </div>
                  {validationErrors.includes('phoneNumber') ? (
                    <small>{errorMessages.phoneNumber}</small>
                  ) : null}
                </label>
              ) : null}
            </div>
          </section>

          {isEditing ? (
            <section className="user-profile__card">
              <div className="user-profile__card-header">
                <span className="user-profile__section-icon">
                  <BellOutlined aria-hidden />
                </span>
                <div>
                  <h2>Notifications</h2>
                  <p>Choose how this user receives recruiting updates.</p>
                </div>
              </div>

              <div className="user-profile__settings-list">
                <div>
                  <span className="user-profile__setting-icon">
                    <MailOutlined aria-hidden />
                  </span>
                  <div>
                    <strong>Email notifications</strong>
                    <small>Interview and workflow updates by email.</small>
                  </div>
                  <Switch
                    checked={user.emailNotifiable}
                    disabled={!canEditDetails}
                    onChange={(checked) =>
                      setUser((current) => ({
                        ...current,
                        emailNotifiable: checked,
                      }))
                    }
                  />
                </div>
                <div>
                  <span className="user-profile__setting-icon">
                    <WhatsAppOutlined aria-hidden />
                  </span>
                  <div>
                    <strong>WhatsApp notifications</strong>
                    <small>Time-sensitive updates on WhatsApp.</small>
                  </div>
                  <Switch
                    checked={user.whatsappNotifiable}
                    disabled={!canEditDetails || !user.phoneNumber}
                    onChange={(checked) =>
                      setUser((current) => ({
                        ...current,
                        whatsappNotifiable: checked,
                      }))
                    }
                  />
                </div>
              </div>
            </section>
          ) : null}
        </div>

        <aside className="user-profile__sidebar">
          <section className="user-profile__card user-profile__access-card">
            <div className="user-profile__card-header">
              <span className="user-profile__section-icon">
                <SafetyCertificateOutlined aria-hidden />
              </span>
              <div>
                <h2>Access</h2>
                <p>Role and workspace availability.</p>
              </div>
            </div>

            <div className="user-profile__role-options" role="radiogroup">
              <button
                type="button"
                role="radio"
                aria-checked={!user.admin}
                className={!user.admin ? 'is-selected' : ''}
                disabled={isEditing && !canEditOwnAccess}
                onClick={() =>
                  setUser((current) => ({
                    ...current,
                    admin: false,
                    roles: ['COMPANY_RECRUITER'],
                  }))
                }
              >
                <TeamOutlined aria-hidden />
                <span>
                  <strong>Recruiter</strong>
                  <small>Manage candidates and interviews.</small>
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={user.admin}
                className={user.admin ? 'is-selected' : ''}
                disabled={!isAdmin || readonly || isCurrentUser}
                onClick={() =>
                  setUser((current) => ({
                    ...current,
                    admin: true,
                    roles: ['COMPANY_ADMIN'],
                  }))
                }
              >
                <LockOutlined aria-hidden />
                <span>
                  <strong>Company admin</strong>
                  <small>Full company and team access.</small>
                </span>
              </button>
            </div>

            {isEditing ? (
              <div className="user-profile__active-setting">
                <div>
                  <strong>Workspace access</strong>
                  <small>
                    {isCurrentUser
                      ? 'Your own access cannot be disabled here.'
                      : 'Disable access without deleting the profile.'}
                  </small>
                </div>
                <Switch
                  checked={user.enabled}
                  disabled={!canEditOwnAccess}
                  onChange={(checked) =>
                    setUser((current) => ({
                      ...current,
                      enabled: checked,
                    }))
                  }
                />
              </div>
            ) : null}
          </section>

          <section className="user-profile__save-card">
            <div className="user-profile__save-state">
              {hasChanges ? (
                <>
                  <span className="user-profile__save-dot" />
                  Unsaved changes
                </>
              ) : (
                <>
                  <CheckCircleOutlined aria-hidden />
                  All changes saved
                </>
              )}
            </div>
            <dl>
              <div>
                <dt>Role</dt>
                <dd>{roleName(user.admin)}</dd>
              </div>
              {isEditing ? (
                <div>
                  <dt>Access</dt>
                  <dd>{user.enabled ? 'Active' : 'Inactive'}</dd>
                </div>
              ) : null}
            </dl>

            <button
              className="company-primary-action"
              type="button"
              disabled={
                saving ||
                !isAdmin ||
                (isEditing ? !hasChanges || readonly : !hasChanges)
              }
              onClick={() => void (isEditing ? updateUser() : sendInvite())}
            >
              <SaveOutlined aria-hidden />
              {saving
                ? 'Saving…'
                : isEditing
                  ? 'Update user'
                  : 'Send invitation'}
            </button>
            {hasChanges && isEditing ? (
              <button
                className="company-secondary-action"
                type="button"
                disabled={saving}
                onClick={resetForm}
              >
                Discard changes
              </button>
            ) : null}
          </section>
        </aside>
      </div>
    </main>
  );
};

export default UserProfile;
